import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
  IconButton,
} from '@mui/material';
import { format } from 'date-fns';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';


import {
  CalendarEvent,
  CreateCalendarEventDto,
  EventCategory,
  ReminderType,
  RecurringPattern,
  RecurringPeriod,
  ReminderUnit,
} from '@/types/calendar';

import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import { useDeleteCalendarEvent } from '@/api/endpoints/calendar/useDeleteCalendarEvent';
import { useUpdateCalendarEvent } from '@/api/endpoints/calendar/useUpdateCalendarEvent';
import { Building01Icon } from '@/assets/icons/Building01Icon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { XCloseIcon } from '@/assets/icons/XCloseIcon';

import FormSelect from '@/components/form/FormSelect';
import FormSwitch from '@/components/form/FormSwitch';
import FormTextField from '@/components/form/FormTextField';
import ModalWithForm from '@/components/modals/ModalWithForm';



// Extended DTO to include recurring_end_date field needed for recurring events
interface ExtendedCalendarEventDto extends CreateCalendarEventDto {
  recurring_end_date?: string;
  deletedReminderIds?: Array<number | string>;
}

interface Props {
  event: CalendarEvent;
  onClose: () => void;
  onEventDeleted?: () => void;
}

interface ReminderIntervalForm {
  id?: number | string;
  value: number;
  unit: ReminderUnit;
  originalRemindAt?: string;
}

interface EditCalendarEventForm {
  id: number;
  title: string;
  description?: string;
  start: string;
  end: string;
  all_day: boolean;
  property_location_id: number;
  building_id?: number;
  unit_id?: number;
  tenant_id?: number;

  category?: EventCategory;
  reminderType?: ReminderType;
  recurringPattern?: RecurringPattern;
  recurringEvery?: number;
  recurringPeriod?: RecurringPeriod;
  neverExpires?: boolean;
  reminderIntervals?: ReminderIntervalForm[];

  notifySelf?: boolean;
  notifyTenant?: boolean;
  notifyVendor?: boolean;
  vendorId?: string;
  vendorEmail?: string;
  vendorMessage?: string;
  emailNotification?: boolean;
  smsNotification?: boolean;
  pushNotification?: boolean;
  dashboardNotification?: boolean;

  // Store the original reminders array for reference
  reminders?: Array<{
    id: number | string;
    calendar_event_id: number;
    remind_at: string;
    type: string;
    unit: string;
    value: number;
    sent: boolean;
    created_at: string;
    updated_at: string;
  }>;

  isDateRange: boolean;
  multipleReminders: boolean;
}

const eventCategories = [
  { label: 'Lease', value: EventCategory.LEASE },
  { label: 'Payment', value: EventCategory.PAYMENT },
  { label: 'Maintenance', value: EventCategory.MAINTENANCE },
  { label: 'Inspection', value: EventCategory.INSPECTION },
  { label: 'Other', value: EventCategory.OTHER },
];

const reminderTypes = [
  { label: 'One-Time Event', value: ReminderType.ONE_TIME },
  { label: 'Recurring Event', value: ReminderType.RECURRING },
];

const recurringPatterns = [
  { label: 'Daily', value: RecurringPattern.DAILY },
  { label: 'Weekly', value: RecurringPattern.WEEKLY },
  { label: 'Monthly', value: RecurringPattern.MONTHLY },
  { label: 'Yearly', value: RecurringPattern.YEARLY },
  { label: 'Custom Interval', value: RecurringPattern.CUSTOM },
];

const periodOptions = [
  { label: 'Days', value: RecurringPeriod.DAYS },
  { label: 'Weeks', value: RecurringPeriod.WEEKS },
  { label: 'Months', value: RecurringPeriod.MONTHS },
  { label: 'Years', value: RecurringPeriod.YEARS },
];

const reminderUnitOptions = [
  { label: 'Days before', value: ReminderUnit.DAYS_BEFORE },
  { label: 'Weeks before', value: ReminderUnit.WEEKS_BEFORE },
  { label: 'Months before', value: ReminderUnit.MONTHS_BEFORE },
];

const CalendarEventEditModal2: React.FC<Props> = ({ event, onClose, onEventDeleted }) => {
  // Check if event spans multiple days
  const hasDateRange = new Date(event.start).toDateString() !== new Date(event.end).toDateString();
  const [isDateRange, setIsDateRange] = useState(hasDateRange);
  const [multipleReminders, setMultipleReminders] = useState((event.reminders?.length ?? 0) > 1);
  const initialReminderType = event.recurringSettings
    ? ReminderType.RECURRING
    : ReminderType.ONE_TIME;
  const initialNeverExpires = event.recurringSettings ? !event.recurringSettings.end_date : true;
  const initialIsRecurrenceEndActive =
    initialReminderType === ReminderType.RECURRING && !initialNeverExpires;

  let initialFormEnd = new Date(event.end).toISOString().split('T')[0];
  if (initialIsRecurrenceEndActive && event.recurringSettings?.end_date) {
    const [dateOnly] = new Date(event.recurringSettings.end_date).toISOString().split('T');
    initialFormEnd = dateOnly;
  }

  // Filter out reminders that are no longer in the event data
  const getActiveReminders = () => {
    if (!event.reminders) return [];

    return event.reminders.map(r => ({
      id: r.id,
      value: r.value,
      unit: r.unit,
      originalRemindAt: r.remind_at,
    }));
  };

  const form = useForm<EditCalendarEventForm>({
    defaultValues: {
      id: event.id,
      title: event.title,
      description: event.description || '',
      start: new Date(event.start).toISOString().split('T')[0],
      end: initialFormEnd,
      all_day: event.all_day,
      property_location_id: event.propertyLocationId,
      building_id: event.buildingId,
      unit_id: event.unitId,
      tenant_id: event.tenantId,
      isDateRange: hasDateRange,
      multipleReminders: (event.reminders?.length ?? 0) > 1,
      category: event.metadata?.category || EventCategory.OTHER,
      reminderType: event.recurringSettings ? ReminderType.RECURRING : ReminderType.ONE_TIME,
      recurringPattern: event.recurringSettings?.pattern || RecurringPattern.DAILY,
      recurringEvery: event.recurringSettings?.every || 1,
      recurringPeriod: event.recurringSettings?.period || RecurringPeriod.DAYS,
      neverExpires: event.recurringSettings ? !event.recurringSettings.end_date : true,
      reminderIntervals: getActiveReminders() || [{ value: 30, unit: ReminderUnit.DAYS_BEFORE }],
      reminders: event.reminders || [],
      notifySelf: event.metadata?.notifySelf || false,
      notifyTenant: event.metadata?.notifyTenant || false,
      notifyVendor: event.metadata?.notifyVendor || false,
      vendorId: event.metadata?.vendor?.id || '',
      vendorEmail: event.metadata?.vendor?.email || '',
      vendorMessage: event.metadata?.vendor?.message || '',
      emailNotification: event.metadata?.emailNotification || false,
      smsNotification: event.metadata?.smsNotification || false,
      pushNotification: event.metadata?.pushNotification || false,
      dashboardNotification: event.metadata?.dashboardNotification || false,
    },
  });

  const { control, watch, setValue, handleSubmit } = form;

  const updateCalendarEventMutation = useUpdateCalendarEvent({
    onSuccess: () => {
      onEventDeleted?.();
      onClose();
    },
  });

  const deleteCalendarEventMutation = useDeleteCalendarEvent({
    onSuccess: () => {
      onEventDeleted?.();
      onClose();
    },
  });

  const handleUpdateSubmit = async (data: EditCalendarEventForm) => {
    // Fix the date conversion issue by explicitly handling the date parts
    const [startYear, startMonth, startDay] = data.start.split('-').map(Number);
    const startDate = new Date(Date.UTC(startYear, startMonth - 1, startDay));

    // Create a separate variable for recurring end date
    let recurringEndDate: Date | undefined;

    // Handle recurring end date separately if this is a recurring event with an end date
    if (data.reminderType === ReminderType.RECURRING && !data.neverExpires) {
      const [endYear, endMonth, endDay] = data.end.split('-').map(Number);
      recurringEndDate = new Date(Date.UTC(endYear, endMonth - 1, endDay));
    }

    // Normal event end date (used for non-recurring events or if neverExpires is true)
    let eventEndDate: Date;
    if (data.all_day) {
      eventEndDate = startDate;
    } else {
      const [endYear, endMonth, endDay] = data.end.split('-').map(Number);
      eventEndDate = new Date(Date.UTC(endYear, endMonth - 1, endDay));
    }

    // Find which reminders are missing from the current form compared to original event data
    const originalReminderIds = new Set();
    if (event.reminders) {
      event.reminders.forEach(reminder => {
        originalReminderIds.add(reminder.id);
      });
    }

    // Get IDs of reminders that are in the form
    const formReminderIds = new Set();
    if (data.reminderIntervals) {
      data.reminderIntervals.forEach(reminder => {
        if (reminder.id) {
          formReminderIds.add(reminder.id);
        }
      });
    }

    // Find reminders that were in the original data but not in the form
    const implicitlyDeletedReminderIds: Array<number | string> = [];
    originalReminderIds.forEach(id => {
      if (!formReminderIds.has(id)) {
        implicitlyDeletedReminderIds.push(id as number | string);
      }
    });

    // Combine with explicitly deleted reminders from this modal
    const allDeletedReminderIds = [...implicitlyDeletedReminderIds];

    // Map reminder intervals with IDs to prevent duplication
    const reminderIntervals = data.reminderIntervals
      ?.filter(
        r =>
          // Keep reminder if it has no ID (new reminder)
          // or if it has an ID that's not in deletedReminderIds
          !r.id || (typeof r.id === 'number' && !allDeletedReminderIds.includes(r.id))
      )
      .map(r => {
        // For existing reminders, include the ID
        if (r.id) {
          return {
            id: r.id,
            value: r.value,
            unit: r.unit,
          };
        }
        // For new reminders, just send value and unit
        return {
          value: r.value,
          unit: r.unit,
        };
      });

    const updatedEvent: Partial<ExtendedCalendarEventDto> = {
      title: data.title,
      description: data.description,
      start: startDate.toISOString(),
      end: recurringEndDate ? recurringEndDate.toISOString() : eventEndDate.toISOString(),
      all_day: data.all_day,
      property_location_id: data.property_location_id,
      building_id: data.building_id,
      unit_id: data.unit_id,
      tenant_id: data.tenant_id,

      category: data.category,
      reminderType: data.reminderType,
      recurringPattern: data.recurringPattern,
      recurringEvery: data.recurringEvery,
      recurringPeriod: data.recurringPeriod,
      neverExpires: data.reminderType === ReminderType.RECURRING ? data.neverExpires : undefined,
      reminderIntervals,
      deletedReminderIds: allDeletedReminderIds,

      notifySelf: data.notifySelf,
      notifyTenant: data.notifyTenant,
      notifyVendor: data.notifyVendor,
      vendorId: data.vendorId,
      vendorEmail: data.vendorEmail,
      vendorMessage: data.vendorMessage,
      emailNotification: data.emailNotification,
      smsNotification: data.smsNotification,
      pushNotification: data.pushNotification,
      dashboardNotification: data.dashboardNotification,
    };

    // For recurring events with an end date, add it to the payload
    if (data.reminderType === ReminderType.RECURRING && !data.neverExpires && recurringEndDate) {
      updatedEvent.recurring_end_date = recurringEndDate.toISOString();
    }

    if (data.reminderType !== ReminderType.RECURRING) {
      delete updatedEvent.recurringPattern;
      delete updatedEvent.recurringEvery;
      delete updatedEvent.recurringPeriod;
      delete updatedEvent.neverExpires;
      delete updatedEvent.recurring_end_date;
    }

    return updateCalendarEventMutation.mutateAsync({
      id: data.id,
      data: updatedEvent,
    });
  };

  const handleDeleteClick = async () => deleteCalendarEventMutation.mutateAsync(event.id);

  const addReminderInterval = () => {
    const currentIntervals = form.getValues('reminderIntervals') || [];
    form.setValue('reminderIntervals', [
      ...currentIntervals,
      { value: 30, unit: ReminderUnit.DAYS_BEFORE },
    ]);
  };

  const removeReminderInterval = (index: number) => {
    const currentIntervals = form.getValues('reminderIntervals') || [];
    form.setValue(
      'reminderIntervals',
      currentIntervals.filter((_, i) => i !== index)
    );
  };

  // Helper function to calculate and display the reminder date
  const calculateReminderDate = (value: number, unit: ReminderUnit) => {
    const startValue = form.watch('start');
    if (!startValue) return '';

    // Get the event start date
    const dt = new Date(startValue);
    const reminderDate = new Date(dt);

    // Calculate the reminder date based on the unit
    switch (unit) {
    case ReminderUnit.DAYS_BEFORE:
      reminderDate.setDate(reminderDate.getDate() - value);
      break;
    case ReminderUnit.WEEKS_BEFORE:
      reminderDate.setDate(reminderDate.getDate() - (value * 7));
      break;
    case ReminderUnit.MONTHS_BEFORE:
      reminderDate.setMonth(reminderDate.getMonth() - value);
      break;
    default:
      break;
    }

    return format(reminderDate, 'MMM d, yyyy');
  };

  // const allDay = watch('all_day');
  const startDate = watch('start');
  const currentReminderType = watch('reminderType');
  const currentRecurringPattern = watch('recurringPattern');
  const currentNotifyVendor = watch('notifyVendor');
  const currentNeverExpires = watch('neverExpires');
  const isRecurrenceEndActive =
    currentReminderType === ReminderType.RECURRING && !currentNeverExpires;

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'isDateRange') {
        setIsDateRange(Boolean(value.isDateRange));
        if (!value.isDateRange && !isRecurrenceEndActive) {
          setValue('end', startDate);
        }
      } else if (name === 'multipleReminders') {
        setMultipleReminders(Boolean(value.multipleReminders));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue, startDate, isRecurrenceEndActive]);

  // Create styles based on Figma design
  const figmaStyles = {
    formSection: {
      p: 1.5,
      mb: 1.5,
      border: '1px solid',
      borderColor: 'grey.200',
      borderRadius: 1,
      boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
      backgroundColor: '#FFFFFF',
    },
    sectionTitle: {
      mb: 1,
      fontWeight: 500,
      fontSize: '14px',
      color: '#414651',
    },
    inputLabel: {
      fontWeight: 500,
      fontSize: '13px',
      color: '#414651',
      mb: 0.5,
    },
    notificationLabel: {
      '& .MuiFormControlLabel-label': {
        fontSize: '13px',
      },
    },
    inputField: {
      '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
        '& fieldset': {
          borderColor: '#D5D7DA',
        },
      },
      '& .MuiInputBase-input': {
        padding: '6px 10px',
        fontSize: '13px',
      },
      '& .MuiInputLabel-root': {
        fontSize: '13px',
        fontWeight: 500,
      },
    },
    formFieldContainer: {
      mb: 1,
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      mb: 0.5,
    },
    twoColumnLayout: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    },
    inlineGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 1,
    },
  };

  return (
    <ModalWithForm
      title="Edit Event"
      onClose={onClose}
      maxWidth="sm"
      form={form}
      onSubmit={handleSubmit(handleUpdateSubmit)}
      buttonColor="primary"
      buttonLabel="Save Changes"
      showCloseButton={false}
    >
      <MuiPageWrapper>
        <Box sx={figmaStyles.formSection}>
          <Typography variant="h6" sx={figmaStyles.sectionTitle}>
            Basic Information
          </Typography>

          <Box sx={figmaStyles.formFieldContainer}>
            <Typography sx={figmaStyles.inputLabel}>Title</Typography>
            <FormTextField
              name="title"
              control={control}
              textFieldProps={{
                fullWidth: true,
                InputProps: {
                  placeholder: 'e.g., HOA Fee Due, Lease Expiration'
                },
                sx: figmaStyles.inputField,
              }}
            />
          </Box>

          <Box sx={figmaStyles.inlineGroup}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={figmaStyles.inputLabel}>Category</Typography>
              <FormSelect
                name="category"
                control={control}
                options={eventCategories}
                muiSelectProps={{
                  sx: figmaStyles.inputField,
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={figmaStyles.inputLabel}>Event Type</Typography>
              <FormSelect
                name="reminderType"
                control={control}
                options={reminderTypes}
                muiSelectProps={{
                  sx: figmaStyles.inputField,
                }}
              />
            </Box>
          </Box>

          <Box sx={figmaStyles.formFieldContainer}>
            <Typography sx={figmaStyles.inputLabel}>Description</Typography>
            <FormTextField
              name="description"
              control={control}
              textFieldProps={{
                fullWidth: true,
                multiline: true,
                minRows: 2,
                maxRows: 6,
                InputProps: {
                  placeholder: 'Enter details about this event or reminder'
                },
                sx: figmaStyles.inputField,
              }}
            />
          </Box>
        </Box>

        <Box sx={figmaStyles.formSection}>
          <Typography variant="h6" sx={figmaStyles.sectionTitle}>
            Schedule
          </Typography>

          <Box sx={figmaStyles.inlineGroup}>
            <FormSwitch
              name="isDateRange"
              control={control}
              controlLabel="Date Range"
              defaultValue={isDateRange}
            />
            {currentReminderType === ReminderType.RECURRING && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentNeverExpires}
                    onChange={e => setValue('neverExpires', e.target.checked)}
                  />
                }
                label="Never Expires"
                sx={{
                  ...figmaStyles.notificationLabel,
                  alignItems: 'center',
                  '& .MuiFormControlLabel-label': {
                    whiteSpace: 'nowrap',
                    fontSize: '12px',
                    lineHeight: 1
                  }
                }}
              />
            )}
          </Box>

          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Box sx={{ flex: 1 }}>
              <FormTextField
                name="start"
                label="Start date"
                control={control}
                textFieldProps={{
                  fullWidth: true,
                  type: 'date',
                  InputLabelProps: { shrink: true },
                  sx: figmaStyles.inputField,
                  onChange: e => {
                    setValue('start', e.target.value);
                    if (!isDateRange && !isRecurrenceEndActive) {
                      setValue('end', e.target.value);
                    } else {
                      const currentEndDate = watch('end');
                      if (new Date(currentEndDate) < new Date(e.target.value)) {
                        setValue('end', e.target.value);
                      }
                    }
                  },
                }}
              />
            </Box>
            {(() => {
              if (isRecurrenceEndActive) {
                return (
                  <Box sx={{ flex: 1 }}>
                    <FormTextField
                      name="end"
                      label="End Date"
                      control={control}
                      textFieldProps={{
                        fullWidth: true,
                        type: 'date',
                        InputLabelProps: { shrink: true },
                        inputProps: { min: startDate },
                        sx: figmaStyles.inputField,
                      }}
                    />
                  </Box>
                );
              }

              if (isDateRange) {
                return (
                  <Box sx={{ flex: 1 }}>
                    <FormTextField
                      name="end"
                      label="End date"
                      control={control}
                      textFieldProps={{
                        fullWidth: true,
                        type: 'date',
                        InputLabelProps: { shrink: true },
                        inputProps: {
                          min: startDate,
                        },
                        sx: figmaStyles.inputField,
                      }}
                    />
                  </Box>
                );
              }

              return null;
            })()}
          </Stack>

          {currentReminderType === ReminderType.RECURRING && (
            <Box sx={{ mt: 1 }}>
              <Box sx={figmaStyles.inlineGroup}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={figmaStyles.inputLabel}>Pattern</Typography>
                  <FormSelect
                    name="recurringPattern"
                    control={control}
                    options={recurringPatterns}
                    muiSelectProps={{
                      sx: figmaStyles.inputField,
                    }}
                  />
                </Box>
                {currentRecurringPattern === RecurringPattern.CUSTOM && (
                  <>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={figmaStyles.inputLabel}>Every</Typography>
                      <FormTextField
                        name="recurringEvery"
                        control={control}
                        textFieldProps={{
                          fullWidth: true,
                          type: 'number',
                          inputProps: { min: 1 },
                          sx: figmaStyles.inputField,
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={figmaStyles.inputLabel}>Period</Typography>
                      <FormSelect
                        name="recurringPeriod"
                        control={control}
                        options={periodOptions}
                        muiSelectProps={{
                          sx: figmaStyles.inputField,
                        }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}

          <Box sx={{ mt: 1 }}>
            <FormSwitch
              name="multipleReminders"
              control={control}
              controlLabel="Multiple Reminders"
              defaultValue={multipleReminders}
            />

            {multipleReminders && (
              <Box sx={{ mt: 1 }}>
                {(form.watch('reminderIntervals') || []).map((interval, index) => (
                  <React.Fragment key={interval.id ? `reminder-${interval.id}` : `reminder-${interval.value}-${interval.unit}-${index}`}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <FormTextField
                        name={`reminderIntervals.${index}.value`}
                        control={control}
                        textFieldProps={{
                          type: 'number',
                          sx: { ...figmaStyles.inputField, width: 80 },
                          inputProps: { min: 1 },
                        }}
                      />
                      <FormSelect
                        control={control}
                        options={reminderUnitOptions}
                        muiSelectProps={{
                          sx: { ...figmaStyles.inputField, minWidth: 120 },
                        }}
                        {...form.register(`reminderIntervals.${index}.unit`)}
                      />
                      <IconButton
                        onClick={() => removeReminderInterval(index)}
                        sx={{ color: 'error.main', p: 0.5 }}
                      >
                        <XCloseIcon />
                      </IconButton>
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', ml: 1, mb: 1, color: 'text.secondary', fontSize: '12px' }}
                    >
                      Reminder date:{' '}
                      {calculateReminderDate(
                        form.watch(`reminderIntervals.${index}.value`) || 0,
                        form.watch(`reminderIntervals.${index}.unit`) || ReminderUnit.DAYS_BEFORE
                      )}
                    </Typography>
                  </React.Fragment>
                ))}
                <Button
                  startIcon={<PlusIcon />}
                  onClick={addReminderInterval}
                  variant="text"
                  color="primary"
                  size="small"
                  sx={{ mt: 0.5 }}
                >
                  Add reminder interval
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={figmaStyles.formSection}>
          <Typography variant="h6" sx={figmaStyles.sectionTitle}>
            Notifications
          </Typography>

          <Box sx={figmaStyles.inlineGroup}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={watch('notifySelf')}
                  onChange={e => setValue('notifySelf', e.target.checked)}
                />
              }
              label="Notify Myself"
              sx={figmaStyles.notificationLabel}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={watch('notifyTenant')}
                  onChange={e => setValue('notifyTenant', e.target.checked)}
                />
              }
              label="Notify Tenant"
              sx={figmaStyles.notificationLabel}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={watch('notifyVendor')}
                  onChange={e => setValue('notifyVendor', e.target.checked)}
                />
              }
              label="Notify Vendor"
              sx={figmaStyles.notificationLabel}
            />
          </Box>

          <Box sx={figmaStyles.inlineGroup}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={watch('emailNotification')}
                  onChange={e => setValue('emailNotification', e.target.checked)}
                />
              }
              label="Email"
              sx={figmaStyles.notificationLabel}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={watch('smsNotification')}
                  onChange={e => setValue('smsNotification', e.target.checked)}
                />
              }
              label="SMS"
              sx={figmaStyles.notificationLabel}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={watch('dashboardNotification')}
                  onChange={e => setValue('dashboardNotification', e.target.checked)}
                />
              }
              label="Dashboard"
              sx={figmaStyles.notificationLabel}
            />
          </Box>

          {currentNotifyVendor && (
            <Box sx={{ mt: 1 }}>
              <Box sx={figmaStyles.inlineGroup}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={figmaStyles.inputLabel}>Vendor ID</Typography>
                  <FormTextField
                    name="vendorId"
                    control={control}
                    textFieldProps={{
                      fullWidth: true,
                      InputProps: {
                        placeholder: 'Vendor ID'
                      },
                      sx: figmaStyles.inputField,
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={figmaStyles.inputLabel}>Vendor Email</Typography>
                  <FormTextField
                    name="vendorEmail"
                    control={control}
                    textFieldProps={{
                      fullWidth: true,
                      type: 'email',
                      InputProps: {
                        placeholder: 'Email'
                      },
                      sx: figmaStyles.inputField,
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ mt: 1 }}>
                <Typography sx={figmaStyles.inputLabel}>Message for Vendor</Typography>
                <FormTextField
                  name="vendorMessage"
                  control={control}
                  textFieldProps={{
                    fullWidth: true,
                    multiline: true,
                    rows: 2,
                    InputProps: {
                      placeholder: 'Message'
                    },
                    sx: figmaStyles.inputField,
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>

        {(event.buildingId || event.unitId || event.tenantId) && (
          <Box sx={figmaStyles.formSection}>
            <Typography variant="h6" sx={figmaStyles.sectionTitle}>
              Associated With
            </Typography>
            <Stack spacing={1}>
              {event.buildingId && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Building01Icon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="dataGridBody">
                    {event.building?.name || event.buildingId}
                  </Typography>
                </Stack>
              )}
              {event.unitId && (
                <Typography variant="dataGridBody">
                  Unit: {event.unit?.name || event.unitId}
                </Typography>
              )}
              {event.tenantId && (
                <Typography variant="dataGridBody">
                  Tenant: {event.tenant?.full_name || event.tenantId}
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        <Button
          variant="outlined"
          color="error"
          onClick={handleDeleteClick}
          sx={{ mt: 2, width: '100%' }}
        >
          Delete Event
        </Button>
      </MuiPageWrapper>
    </ModalWithForm>
  );
};

export default CalendarEventEditModal2; 